import React, { useState, useEffect } from 'react';
import { 
  Drawer, Box, Typography, TextField, Button, Stack,
  FormControl, InputLabel, Select, MenuItem,
  Divider
} from '@mui/material';
import { fget } from '../../axios/axiosCalls';
import toast from 'react-hot-toast';

const CustomerEditDrawer = ({ open, onClose, customer, onSave }) => {
    const [colleges, setColleges] = useState([]);
    const [campuses, setCampuses] = useState([]);
    const [countries, setCountries] = useState([]);
    const fetchCountries = async () => {
        try {
          const response = await fget({ url: '/api/v2/countries' });
          if (response.data.status === 'Success') {
            setCountries(response.data.countries);
          }
        } catch (error) {
          toast.error('Error fetching countries');
        }
      };
      
      useEffect(() => {
        fetchCountries();
      }, [customer]);
      
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    toCountry: '',
    type: '',
    dob: '',
    gender: '',
    nationality: '',
    resCountry: '',
    state: '',
    city: '',
    college: '',
    campus: '',
    course: '',
    intake: '',
    languages: [],
    mothertongue: '',
    intakeYear: '',
    collegeId: '',
    campusId: '',
    guarantorType: 'local',
    guarantorName: '',
    guarantorEmail: '',
    guarantorPhone: '',
    guarantorCountry: ''
  });
  const fetchColleges = async () => {
    
    try {
        
      const response = await fget({ url: '/api/v2/colleges', params: {countryId: formData?.toCountry} });
      if (response.data.status === 'Success') {
        setColleges(response.data.colleges);
      }
    } catch (error) {
      toast.error('Error fetching colleges');
    }
  };
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [countryAllOptions, setCountryAllOptions] = useState([]);
  useEffect(() => {
    const fetchAllCountries = async () => {
      try {
        const response = await fget({ url: '/api/v2/countries?all=1' });
        // Axios responses have the data in 'data' property
        if (response.status === 200) {
          const countries = response.data.data.map((country,index )=> ({
            name: country.cname,
            id: index,
            states: country.states || [], // Extract states,
            nationality: country.nationality
          }));
          // Sort countries alphabetically
          countries.sort((a, b) => a.name.localeCompare(b.name));
          setCountryAllOptions(countries);
        } else {
          throw new Error(`Error: ${response.status}`);
        }
      } catch (error) {
        toast.error('Failed to fetch countries.');
        console.error('Fetch Countries Error:', error);
      } finally {
        setLoadingCountries(false);
      }
    };

    fetchAllCountries();
  }, []);
  const getCollegeIdByName = async (collegeName, countryId) => {
    try {
      // Get all colleges for the country
      const response = await fget({
        url: '/api/v2/colleges',
        params: { countryId }
      });
  
      if (response.data.status === 'Success') {
        // Find matching college
        const college = response.data.colleges.find(
          c => c.name.toLowerCase() === collegeName.toLowerCase()
        );
        
        return college?.collegeId || null;
      }
      
      return null;
    } catch (error) {
      console.error('Error getting college ID:', error);
      return null;
    }
  };
  // Add fetch campuses function
  const fetchCampuses = async (collegeName) => {
    try {
      const collegeId = await getCollegeIdByName(collegeName, formData.toCountry);

      const response = await fget({ 
        url: `/api/v2/campuses`, 
        params: { 
          collegeId: collegeId,
          countryId: formData.toCountry 
        } 
      });
      if (response.data.status === 'Success') {
        const formattedCampuses = response.data.campuses.map(campus => ({
          id: campus.campusId,
          name: campus.name
        }));
        
        setCampuses(formattedCampuses);
      }
    } catch (error) {
      console.log('Error fetching campuses:', error);
      toast.error('Error fetching campuses');
    }
  };

  // Add useEffect for initial college fetch
  useEffect(() => {
    fetchColleges();
  }, [formData.toCountry]);

  // Add useEffect for campus fetch when college changes
  useEffect(() => {
    if (formData.collegeId && formData.toCountry) {
      fetchCampuses(formData.collegeId);
    }
  }, [formData.collegeId, formData.toCountry]);
  
  useEffect(() => {
    const setInitialFormData = async () => {
      if (customer) {
        const initialData = {
          ...customer,
          languages: customer.languages || [],
        guarantorType: customer.guarantor?.type || 'local',
        guarantorName: customer.guarantor?.name || '',
        guarantorEmail: customer.guarantor?.email || '',
        guarantorPhone: customer.guarantor?.phone || '',
        guarantorCountry: customer.guarantor?.country || ''
        };
  
        if (customer.toCountry) {
          try {
            const response = await fget({ 
              url: '/api/v2/colleges', 
              params: { countryId: customer.toCountry } 
            });
            
            if (response.data.status === 'Success') {
              const colleges = response.data.colleges;
              // Find college from list of colleges
              const matchingCollege = colleges.find(c => c.name === customer.college);
              
              if (matchingCollege) {
                initialData.collegeId = matchingCollege.name;
                
                // Find campus from college's campuses
                const matchingCampus = matchingCollege.campuses?.find(
                  c => c.campusName === customer.campus
                );
                
                if (matchingCampus) {
                  initialData.campusId = matchingCampus.campusName;
                }
                const formattedCampuses = matchingCollege.campuses.map(campus => ({
                  id: campus.id,
                  name: campus.campusName
                }));
                
                // Update campuses state
                setCampuses(formattedCampuses || []);
              }
            }
          } catch (error) {
            toast.error('Error fetching college data');
          }
        }
        
        setFormData(initialData);
      }
    };
  
    setInitialFormData();
  }, [customer]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ 
        sx: { 
          width: { xs: '100%', sm: '400px' },
          p: { xs: 2, sm: 3 },
          overflowY: 'auto'
        } 
      }}
    >
      <Box component="form" onSubmit={handleSubmit}>
        <Typography variant="h6" gutterBottom>Edit Student</Typography>
        
        {/* Personal Information */}
        <Typography variant="subtitle2" sx={{ mt: 3, mb: 2 }}>Personal Information</Typography>
        <Stack spacing={3}>
          <TextField
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            fullWidth
          />
          <TextField
            label="Date of Birth"
            type="date"
            value={formData.dob}
            onChange={(e) => setFormData({...formData, dob: e.target.value})}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <FormControl fullWidth>
            <InputLabel>Gender</InputLabel>
            <Select
              value={formData.gender}
              onChange={(e) => setFormData({...formData, gender: e.target.value})}
              label="Gender"
            >
              <MenuItem value="male">Male</MenuItem>
              <MenuItem value="female">Female</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
          {/* <FormControl fullWidth sx={{ mt: 3 }}>
                <InputLabel>Type</InputLabel>
                <Select
                value={formData.type}
                onChange={(e) => setFormData({...formData, type: e.target.value})}
                label="Type"
                >
                <MenuItem value="lead">Lead</MenuItem>
                <MenuItem value="tenant">Tenant</MenuItem>
                </Select>
            </FormControl> */}

            <FormControl fullWidth sx={{ mt: 3 }}>
                <InputLabel>To Country</InputLabel>
                <Select
                value={formData.toCountry}
                onChange={(e) => setFormData({...formData, toCountry: e.target.value})}
                label="To Country"
                >
                {countries.map((country) => (
                    <MenuItem key={country.id} value={country.id}>
                    {country.cname}
                    </MenuItem>
                ))}
                </Select>
            </FormControl>
        </Stack>

        <Divider sx={{ my: 3 }} />

        {/* Contact Details */}
        <Typography variant="subtitle2" sx={{ mb: 2 }}>Contact Details</Typography>
        <Stack spacing={3}>
          <TextField
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            fullWidth
          />
          <TextField
            label="Phone"
            value={formData.phone}
            onChange={(e) => setFormData({...formData, phone: e.target.value})}
            fullWidth
          />
        </Stack>
        
        <Divider sx={{ my: 3 }} />

        {/* Location Details */}
        <Typography variant="subtitle2" sx={{ mb: 2 }}>Location Details</Typography>
        <Stack spacing={3}>
          <TextField
            label="Nationality"
            value={formData.nationality}
            onChange={(e) => setFormData({...formData, nationality: e.target.value})}
            fullWidth
          />
          <TextField
            label="Residence Country"
            value={formData.resCountry}
            onChange={(e) => setFormData({...formData, resCountry: e.target.value})}
            fullWidth
          />
          <TextField
            label="State"
            value={formData.state}
            onChange={(e) => setFormData({...formData, state: e.target.value})}
            fullWidth
          />
          <TextField
            label="City"
            value={formData.city}
            onChange={(e) => setFormData({...formData, city: e.target.value})}
            fullWidth
          />
        </Stack>

        <Divider sx={{ my: 3 }} />

        {/* Education Details */}
        <Typography variant="subtitle2" sx={{ mb: 2 }}>Education Details</Typography>
        <Stack spacing={3}>
        <FormControl fullWidth>
              <InputLabel>College</InputLabel>
              <Select
                value={formData.collegeId}
                onChange={(e) => setFormData({
                  ...formData, 
                  collegeId: e.target.value,
                  campusId: '' // Reset campus when college changes
                })}
                label="College"
              >
                {colleges.map((college) => (
                  <MenuItem key={college.collegeId} value={college.name}>
                    {college.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Campus</InputLabel>
              <Select
                value={formData.campusId}
                onChange={(e) => setFormData({...formData, campusId: e.target.value,})}
                label="Campus"
                disabled={!formData.collegeId}
              >
                {campuses.map((campus) => (
                  <MenuItem key={campus.id} value={campus.name}>
                    {campus.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          <TextField
            label="Course"
            value={formData.course}
            onChange={(e) => setFormData({...formData, course: e.target.value})}
            fullWidth
          />
          <TextField
            label="Intake"
            value={formData.intake}
            onChange={(e) => setFormData({...formData, intake: e.target.value})}
            fullWidth
          />
          <TextField
            label="Intake Year"
            value={formData.intakeYear}
            onChange={(e) => setFormData({...formData, intakeYear: e.target.value})}
            fullWidth
          />
        </Stack>

        <Divider sx={{ my: 3 }} />

        {/* Guarantor Details */}
        <Typography variant="subtitle2" sx={{ mb: 2 }}>Guarantor Details</Typography>
        <Stack spacing={3}>
          <FormControl fullWidth>
            <InputLabel>Guarantor Type</InputLabel>
            <Select
              value={formData.guarantorType}
              onChange={(e) => setFormData({...formData, guarantorType: e.target.value})}
              label="Guarantor Type"
            >
              <MenuItem value="local">Local</MenuItem>
              <MenuItem value="international">International</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Guarantor Name"
            value={formData.guarantorName}
            onChange={(e) => setFormData({...formData, guarantorName: e.target.value})}
            fullWidth
          />

          <TextField
            label="Guarantor Email"
            type="email"
            value={formData.guarantorEmail}
            onChange={(e) => setFormData({...formData, guarantorEmail: e.target.value})}
            fullWidth
          />

          <TextField
            label="Guarantor Phone"
            value={formData.guarantorPhone}
            onChange={(e) => setFormData({...formData, guarantorPhone: e.target.value})}
            fullWidth
          />

         
          {formData.guarantorType === "international" && (
            <FormControl fullWidth>
              <InputLabel>Guarantor Country</InputLabel>
              <Select
                value={formData.guarantorCountry}
                onChange={(e) => setFormData({ ...formData, guarantorCountry: e.target.value })}
                label="Guarantor Country"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {countryAllOptions.map((country) => (
                  <MenuItem key={country.id} value={country.name}>
                    {country.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>

        <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            fullWidth
          >
            Save Changes
          </Button>
          <Button 
            onClick={onClose}
            variant="outlined"
            fullWidth
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default CustomerEditDrawer;